import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  startAfter,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebaseConfig";

const nf = new Intl.NumberFormat("fr-FR");

/* ---------------- Labels ---------------- */
const STATUS_LABELS = {
  pending: "En attente (online)",
  pending_payment_online: "En attente paiement (online)",
  awaiting_payment_offline: "En attente (COD/Pickup)",
  paid: "Payée",
  confirmed: "Confirmée",
  preparing: "Préparation",
  shipped: "Expédiée",
  ready_for_pickup: "Prête au retrait",
  delivered: "Livrée",
  picked_up: "Retirée",
  cancelled: "Annulée",
  payment_failed: "Paiement échoué",
};

const PAYMENT_LABELS = {
  COD: "Paiement à la livraison",
  PICKUP: "Paiement au retrait",
  MOBILE_MONEY: "Mobile Money",
};

const badgeClass = (status) => {
  const s = String(status || "");
  if (["paid", "confirmed", "delivered", "picked_up"].includes(s)) return "text-bg-success";
  if (["payment_failed", "cancelled"].includes(s)) return "text-bg-danger";
  if (["awaiting_payment_offline", "pending", "pending_payment_online"].includes(s)) return "text-bg-warning";
  return "text-bg-secondary";
};

function safeStr(v) {
  return String(v || "").trim();
}

/* ---------------- Helpers produits commande ---------------- */
const moneyXof = (v) => `${nf.format(Math.round(Number(v || 0)))} FCFA`;

const itemImage = (it) =>
  it?.image || it?.img || it?.photo || it?.images?.[0] || "/no-photo.jpg";

const itemCode = (it) =>
  it?.code || it?.reference || it?.sku || it?.barcode || "—";

const itemTitle = (it) =>
  it?.title || it?.name || it?.productTitle || "Article";

const itemQty = (it) => Number(it?.qty ?? it?.quantity ?? 1) || 1;

const itemPrice = (it) =>
  Number(it?.price_xof ?? it?.price ?? it?.unit_price ?? it?.unitPrice ?? 0) || 0;

const getOrderItems = (o) => {
  const a =
    Array.isArray(o?.items) ? o.items :
      Array.isArray(o?.products) ? o.products :
        Array.isArray(o?.cart?.items) ? o.cart.items :
          Array.isArray(o?.lines) ? o.lines :
            [];
  return a;
};

export default function AdminOrders() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtres
  const [qText, setQText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all"); // COD | PICKUP | paygate

  // Pagination
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 25;

  // Modale
  const [selectedOrder, setSelectedOrder] = useState(null);

  const openOrder = (o) => setSelectedOrder(o);
  const closeOrder = () => setSelectedOrder(null);

  // Construire la requête Firestore (version simple)
  const buildQuery = (afterDoc = null) => {
    let baseClauses = [];

    // status
    if (statusFilter !== "all") baseClauses.push(where("status", "==", statusFilter));

    // method
    if (methodFilter !== "all") {
      if (methodFilter === "paygate") baseClauses.push(where("provider", "==", "paygate"));
      else baseClauses.push(where("payment_method", "==", methodFilter));
    }

    // tri/pagination
    baseClauses.push(orderBy("updated_at", "desc"));
    baseClauses.push(limit(PAGE_SIZE));

    let q = query(collection(db, "orders"), ...baseClauses);
    if (afterDoc) q = query(q, startAfter(afterDoc));
    return q;
  };

  const fetchFirst = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(buildQuery(null));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRows(list);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } finally {
      setLoading(false);
    }
  };

  const fetchMore = async () => {
    if (!hasMore || !lastDoc) return;
    const snap = await getDocs(buildQuery(lastDoc));
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setRows((prev) => [...prev, ...list]);
    setLastDoc(snap.docs[snap.docs.length - 1] || lastDoc);
    setHasMore(snap.docs.length === PAGE_SIZE);
  };

  useEffect(() => {
    fetchFirst();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, methodFilter]);

  // Filtre local (search input)
  const filteredLocal = useMemo(() => {
    const s = qText.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((o) => {
      const id = safeStr(o.orderId || o.id).toLowerCase();
      const name = safeStr(o.customer?.name).toLowerCase();
      const phone = safeStr(o.customer?.phone).toLowerCase();
      return id.includes(s) || name.includes(s) || phone.includes(s);
    });
  }, [rows, qText]);

  const updateStatus = async (orderId, newStatus) => {
    if (!orderId) return;

    const nowIso = new Date().toISOString();

    await updateDoc(doc(db, "orders", String(orderId)), {
      status: newStatus,
      updated_at: nowIso,
      updatedAt: serverTimestamp(),
      admin_last_action: { type: "status_update", status: newStatus, at: nowIso },
    });

    // refresh local row
    setRows((prev) =>
      prev.map((r) => (r.id === orderId ? { ...r, status: newStatus, updated_at: nowIso } : r))
    );

    // refresh modale si ouverte
    setSelectedOrder((prev) =>
      prev && prev.id === orderId ? { ...prev, status: newStatus, updated_at: nowIso } : prev
    );
  };

  return (
    <div className="container py-4">
      <style>{`
        .min-w-0{min-width:0}
        .order-modal-backdrop{
          position: fixed; inset:0; background: rgba(0,0,0,.45); z-index: 1055;
        }
        .order-modal{
          position: fixed; inset:0; z-index: 1060; display:flex; align-items:center; justify-content:center;
          padding: 16px;
        }
        .order-modal .modal-dialog{ width:min(980px, 100%); }
        .order-item{
          transition: transform .12s ease, box-shadow .12s ease;
        }
        .order-item:hover{
          transform: translateY(-1px);
          box-shadow: 0 10px 20px rgba(0,0,0,.08);
        }
      `}</style>

      {/* Header + filtres */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h3 className="m-0">Commandes</h3>
          <div className="text-muted small">Gestion COD / PICKUP / PayGate</div>
        </div>

        <div className="d-flex gap-2 flex-wrap">
          <input
            className="form-control"
            style={{ maxWidth: 320 }}
            placeholder="Rechercher (ID / nom / tel)"
            value={qText}
            onChange={(e) => setQText(e.target.value)}
          />

          <select
            className="form-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Tous statuts</option>
            {Object.keys(STATUS_LABELS).map((k) => (
              <option key={k} value={k}>
                {STATUS_LABELS[k]}
              </option>
            ))}
          </select>

          <select
            className="form-select"
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
          >
            <option value="all">Tous modes</option>
            <option value="COD">COD (livraison)</option>
            <option value="PICKUP">PICKUP (retrait)</option>
            <option value="paygate">PayGate (online)</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive card">
        <table className="table table-hover align-middle m-0">
          <thead className="table-light">
            <tr>
              <th>ID</th>
              <th>Client</th>
              <th>Paiement</th>
              <th>Mode</th>
              <th>Montant</th>
              <th>Statut</th>
              <th>MàJ</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="text-center text-muted py-4">
                  Chargement…
                </td>
              </tr>
            ) : filteredLocal.length ? (
              filteredLocal.map((o) => {
                const method =
                  o.payment_method || (o.provider === "paygate" ? "MOBILE_MONEY" : "—");

                const mode =
                  o.delivery?.mode ||
                  (method === "PICKUP" ? "pickup" : method === "COD" ? "delivery" : "—");

                const amount = Number(o.amount_xof ?? o.total_xof ?? o.subtotal_xof ?? 0);

                return (
                  <tr key={o.id}>
                    <td className="fw-semibold">{o.orderId || o.id}</td>

                    <td>
                      <div className="fw-semibold">{o.customer?.name || "—"}</div>
                      <div className="text-muted small">{o.customer?.phone || "—"}</div>
                    </td>

                    <td>
                      <span className="badge text-bg-light border text-dark">
                        {PAYMENT_LABELS[method] || method}
                      </span>
                      {o.provider_reference ? (
                        <div className="text-muted small">Ref: {o.provider_reference}</div>
                      ) : null}
                    </td>

                    <td className="text-muted">{mode}</td>

                    <td className="fw-semibold">{moneyXof(amount)}</td>

                    <td>
                      <span className={`badge ${badgeClass(o.status)}`}>
                        {STATUS_LABELS[o.status] || o.status || "—"}
                      </span>
                    </td>

                    <td className="text-muted small">
                      {o.updated_at ? String(o.updated_at).slice(0, 19).replace("T", " ") : "—"}
                    </td>

                    {/* <td className="text-end">
                      <div className="btn-group">
                        <button
                          className="btn btn-outline-dark btn-sm"
                          onClick={() => openOrder(o)}
                          title="Voir les produits"
                        >
                          Voir produits
                        </button>

                        <button
                          className="btn btn-outline-primary btn-sm dropdown-toggle"
                          type="button"
                          data-bs-toggle="dropdown"
                        >
                          Statut
                        </button>

                        <ul className="dropdown-menu dropdown-menu-end">
                          {Object.keys(STATUS_LABELS).map((s) => (
                            <li key={s}>
                              <button className="dropdown-item" onClick={() => updateStatus(o.id, s)}>
                                {STATUS_LABELS[s]}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </td> */}
                    <td className="text-end">
                      <div className="d-flex justify-content-end gap-2 flex-wrap">
                        <button
                          className="btn btn-outline-dark btn-sm"
                          onClick={() => openOrder(o)}
                          title="Voir les produits"
                        >
                          Voir produits
                        </button>

                        <select
                          className="form-select form-select-sm"
                          style={{ width: 220 }}
                          value={o.status || "pending"}
                          onChange={(e) => updateStatus(o.id, e.target.value)}
                          aria-label="Changer le statut"
                        >
                          {Object.keys(STATUS_LABELS).map((s) => (
                            <option key={s} value={s}>
                              {STATUS_LABELS[s]}
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>

                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={8} className="text-center text-muted py-4">
                  Aucune commande.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Load more */}
      <div className="d-flex justify-content-center mt-3">
        <button className="btn btn-outline-secondary" disabled={!hasMore || loading} onClick={fetchMore}>
          {hasMore ? "Charger plus" : "Fin"}
        </button>
      </div>

      {/* ---------------- Modal produits ---------------- */}
      {selectedOrder && (
        <>
          <div className="order-modal-backdrop" onClick={closeOrder} />
          <div className="order-modal bg-light" role="dialog" aria-modal="true">
            <div className="modal-dialog">
              <div className="modal-content border-0 shadow">
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title mb-0">
                      Commande #{selectedOrder.orderId || selectedOrder.id}
                    </h5>
                    <div className="text-muted small">
                      {selectedOrder.customer?.name || "—"} • {selectedOrder.customer?.phone || "—"}
                    </div>
                  </div>
                  <button type="button" className="btn-close" onClick={closeOrder} aria-label="Close" />
                </div>

                <div className="modal-body">
                  {/* Résumé */}
                  <div className="row g-2 mb-3">
                    <div className="col-12 col-md-4">
                      <div className="p-2 rounded bg-light border">
                        <div className="text-muted small">Montant</div>
                        <div className="fw-bold">
                          {moneyXof(selectedOrder.amount_xof ?? selectedOrder.total_xof ?? selectedOrder.subtotal_xof ?? 0)}
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-md-4">
                      <div className="p-2 rounded bg-light border">
                        <div className="text-muted small">Paiement</div>
                        <div className="fw-semibold">
                          {(() => {
                            const method =
                              selectedOrder.payment_method ||
                              (selectedOrder.provider === "paygate" ? "MOBILE_MONEY" : "");
                            return PAYMENT_LABELS[method] || method || (selectedOrder.provider === "paygate" ? "PayGate" : "—");
                          })()}
                        </div>
                        {selectedOrder.provider_reference ? (
                          <div className="text-muted small">Ref: {selectedOrder.provider_reference}</div>
                        ) : null}
                      </div>
                    </div>

                    <div className="col-12 col-md-4">
                      <div className="p-2 rounded bg-light border">
                        <div className="text-muted small">Statut</div>
                        <span className={`badge ${badgeClass(selectedOrder.status)}`}>
                          {STATUS_LABELS[selectedOrder.status] || selectedOrder.status || "—"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Produits */}
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="m-0">Produits</h6>
                    <span className="text-muted small">
                      {getOrderItems(selectedOrder).length} article(s)
                    </span>
                  </div>

                  <div className="vstack gap-2">
                    {getOrderItems(selectedOrder).map((it, idx) => {
                      const qty = itemQty(it);
                      const price = itemPrice(it);
                      const lineTotal = qty * price;

                      return (
                        <div
                          key={idx}
                          className="d-flex gap-3 p-2 border rounded-3 align-items-center order-item"
                        >
                          <img
                            src={itemImage(it)}
                            alt={itemTitle(it)}
                            width="56"
                            height="56"
                            style={{ objectFit: "cover", borderRadius: 10 }}
                            onError={(e) => (e.currentTarget.src = "/no-photo.jpg")}
                          />

                          <div className="flex-grow-1 min-w-0">
                            <div className="fw-semibold text-truncate">{itemTitle(it)}</div>
                            <div className="text-muted small">
                              Code : <b>{itemCode(it)}</b>
                            </div>
                          </div>

                          <div className="text-end">
                            <div className="small text-muted">Qté</div>
                            <div className="fw-semibold">{qty}</div>
                          </div>

                          <div className="text-end" style={{ minWidth: 140 }}>
                            <div className="small text-muted">Prix</div>
                            <div className="fw-semibold">{moneyXof(price)}</div>
                            <div className="text-muted small">Total: {moneyXof(lineTotal)}</div>
                          </div>
                        </div>
                      );
                    })}

                    {getOrderItems(selectedOrder).length === 0 && (
                      <div className="text-muted small">Aucun produit trouvé dans cette commande.</div>
                    )}
                  </div>
                </div>

                <div className="modal-footer">
                  <div className="me-auto d-flex align-items-center gap-2 flex-wrap">
                    <span className="text-muted small">Changer le statut :</span>

                    <select
                      className="form-select form-select-sm"
                      style={{ width: 260 }}
                      value={selectedOrder.status || "pending"}
                      onChange={(e) => updateStatus(selectedOrder.id, e.target.value)}
                    >
                      {Object.keys(STATUS_LABELS).map((s) => (
                        <option key={s} value={s}>
                          {STATUS_LABELS[s]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button className="btn btn-secondary" onClick={closeOrder}>
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
